import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore/lite";
import { db } from "../Firebase";
import { BiRightArrowAlt, BiCheck } from "react-icons/bi";
import logoBlackSquare from "../assets/logo/cork-matters-logo-2.svg";
import QRCode from "../assets/qr-code-catalogue-grey.png";
import styles from "./Footer.module.scss";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import facebookLogo from "../assets/footer/facebookLogo.svg";
import instagramLogo from "../assets/footer/instaLogo.svg";
import CVLogo from "../assets/logo/CVLogo2.svg";

// Simplified link list for the mobile footer (mirrors the mobile menu style).
const footerMobileLinks = [
  { name: "Shop", url: "/products/all" },
  { name: "About us", url: "/about" },
  { name: "Your Project", url: "/project" },
  { name: "Cork", url: "/cork" },
  { name: "Contact Us", url: "/contact" },
  { name: "Terms and Conditions", url: "/termsandconditions" },
  { name: "Privacy Policy", url: "/privacypolicy" },
  { name: "Shipping and Delivery", url: "/delivery" },
  { name: "Returns Policy", url: "/returnspolicy" },
];

function Footer() {
  const currentYear = new Date().getFullYear();
  const footerRef = useRef(null);
  const footerLogoRef = useRef(null);
  const footerBottomRef = useRef(null);
  const footerMobileRef = useRef(null);

  // --- Subscription Logic ---
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(""); // "success" | "error" | ""

  const handleEmailSubmit = async () => {
    const trimmedEmail = email.trim();
    if (
      !trimmedEmail ||
      !trimmedEmail.includes("@") ||
      !trimmedEmail.includes(".")
    ) {
      setSubmitStatus("error");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("");

    try {
      await addDoc(collection(db, "subscribers"), {
        email: trimmedEmail.toLowerCase(),
        subscribedAt: serverTimestamp(),
        source: "footer",
      });

      setSubmitStatus("success");
      setEmail("");
      // Mark as subscribed so the Popup Prompt stays hidden
      localStorage.setItem("hasSubscribed", "true");
    } catch (error) {
      console.error("Footer subscription error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Animations ---
  useGSAP(() => {
    gsap.from(footerLogoRef.current.children, {
      y: 100,
      opacity: 0,
      stagger: 0.15,
      ease: "power2.out",
      duration: 1,
      delay: 0.4,
      scrollTrigger: {
        trigger: footerLogoRef.current,
        start: "bottom bottom",
        toggleActions: "play none none none",
      },
    });

    gsap.from(footerRef.current.children, {
      y: 100,
      opacity: 0,
      stagger: 0.15,
      ease: "power2.out",
      duration: 1,
      delay: 0.4,
      scrollTrigger: {
        trigger: footerRef.current,
        start: "bottom bottom",
        toggleActions: "play none none none",
      },
    });

    gsap.from(footerBottomRef.current.children, {
      x: 100,
      opacity: 0,
      stagger: 0.15,
      ease: "power2.out",
      duration: 1,
      delay: 0.4,
      scrollTrigger: {
        trigger: footerBottomRef.current,
        start: "bottom bottom",
        toggleActions: "play none none none",
      },
    });

    // Mobile footer: stagger each tagged item in. Only runs when the mobile
    // footer is actually visible (display:none on desktop → offsetParent null),
    // and uses y (not x) so it can't widen the page.
    if (
      footerMobileRef.current &&
      footerMobileRef.current.offsetParent !== null
    ) {
      const items = footerMobileRef.current.querySelectorAll(
        "[data-footer-stagger]"
      );
      gsap.from(items, {
        y: 40,
        opacity: 0,
        stagger: 0.08,
        ease: "power2.out",
        duration: 0.8,
        scrollTrigger: {
          trigger: footerMobileRef.current,
          start: "top 85%",
          toggleActions: "play none none none",
        },
      });
    }
  });

  return (
    <div className={styles.footer}>
      <div className={styles.footerMain}>
        <div className={styles.footerUpperContainer}>
          <div className={styles.grid}>
            <div className={styles.footerLogoContainer} ref={footerLogoRef}>
              <img src={logoBlackSquare} alt="Cork Matters Logo" />
            </div>
            <div className={styles.footerUpperSection} ref={footerRef}>
              <div className={styles.footerColumn3}>
                <p className={styles.footerColumnTitle}>Cork Matters</p>
                <p className={styles.noPaddingTop}>
                  Carlos Santos Mendonça LDA
                </p>
                <p>
                  Rua do Rosário 143 - 145 <br /> 4050-519 Porto <br /> Portugal
                </p>
                <p>+351 91 966 1000</p>
                <div className={styles.linkContainer}>
                  <Link
                    to="mailto:corkmatters.online@gmail.com"
                    className={styles.underlineLink}
                  >
                    corkmatters.online@gmail.com
                  </Link>
                </div>
              </div>

              <div className={styles.footerColumn1}>
                <p className={styles.footerColumnTitle}>Shop</p>
                <div className={styles.linkContainer}>
                  <Link
                    to="/products/all#lighting"
                    className={styles.underlineLink}
                  >
                    Lighting
                  </Link>
                </div>
                <div className={styles.linkContainer}>
                  <Link
                    to="/products/all#office"
                    className={styles.underlineLink}
                  >
                    Office
                  </Link>
                </div>
                <div className={styles.linkContainer}>
                  <Link
                    to="/products/all#tableware"
                    className={styles.underlineLink}
                  >
                    Tableware
                  </Link>
                </div>
                <div className={styles.linkContainer}>
                  <Link
                    to="/products/all#home-living"
                    className={styles.underlineLink}
                  >
                    Home Living
                  </Link>
                </div>
                <div className={styles.linkContainer}>
                  <Link
                    to="/products/all#eva-collection"
                    className={styles.underlineLink}
                  >
                    Eva
                  </Link>
                </div>
              </div>

              <div className={styles.footerColumn1}>
                <p className={styles.footerColumnTitle}>About us</p>
                <div className={styles.linkContainer}>
                  <Link to="/about" className={styles.underlineLink}>
                    Our Designers
                  </Link>
                </div>
              </div>

              <div className={styles.footerColumn1}>
                <p className={styles.footerColumnTitle}>Your Project</p>
                <div className={styles.linkContainer}>
                  <Link to="/project#proposal" className={styles.underlineLink}>
                    Proposal
                  </Link>
                </div>
              </div>

              <div className={styles.footerColumn1}>
                <p className={styles.footerColumnTitle}>Cork</p>
                <div className={styles.linkContainer}>
                  <Link to="/cork" className={styles.underlineLink}>
                    History
                  </Link>
                </div>
              </div>

              <div className={styles.footerColumn2}>
                <p className={styles.footerColumnTitle}>Customer Services</p>
                <div className={styles.linkContainer}>
                  <Link to="/contact" className={styles.underlineLink}>
                    Contact Us
                  </Link>
                </div>
                <div className={styles.linkContainer}>
                  <Link
                    to="/termsandconditions"
                    className={styles.underlineLink}
                  >
                    Terms and Conditions
                  </Link>
                </div>
                <div className={styles.linkContainer}>
                  <Link to="/privacypolicy" className={styles.underlineLink}>
                    Privacy Policy
                  </Link>
                </div>
                <div className={styles.linkContainer}>
                  <Link to="/delivery" className={styles.underlineLink}>
                    Shipping and Delivery
                  </Link>
                </div>
                <div className={styles.linkContainer}>
                  <Link to="/returnspolicy" className={styles.underlineLink}>
                    Returns Policy
                  </Link>
                </div>
              </div>

              <div className={styles.footerColumn1}>
                <p className={styles.footerColumnTitle}>Connect</p>
                <div className={styles.socialLinkContainer}>
                  <a href="https://instagram.com/corkmatters_r">
                    <img src={instagramLogo} alt="Instagram" />
                  </a>
                  <a href="https://www.facebook.com/p/Cork-Matters-100073677396245/">
                    <img src={facebookLogo} alt="Facebook" />
                  </a>
                </div>
              </div>

              <div className={styles.footerColumn2}>
                <p className={styles.footerColumnTitle}>Newsletter</p>
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="email"
                    placeholder="Email address"
                    className={styles.footerEmailInput}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                    disabled={isSubmitting}
                  />
                  <button
                    onClick={handleEmailSubmit}
                    disabled={isSubmitting}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      position: "absolute",
                      right: "10px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {isSubmitting ? "..." : <BiRightArrowAlt size={20} color={"black"} />}
                  </button>
                </div>

                {submitStatus === "success" && (
                  <p
                    style={{
                      color: "#131313",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    Thank you for subscribing!
                  </p>
                )}
                {submitStatus === "error" && (
                  <p
                    style={{
                      color: "#f44336",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    Invalid email or error.
                  </p>
                )}

                <p
                  className={styles.footerColumnTitle}
                  style={{ marginTop: "20px" }}
                >
                  Download our Catalogue
                </p>
                <div className={styles.qrCodeContainer}>
                  <img
                    src={QRCode}
                    alt="QR Code"
                    className={styles.footerQrCode}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footerMobile} ref={footerMobileRef}>
          <div className={styles.footerMobileLogo} data-footer-stagger>
            <img src={logoBlackSquare} alt="Cork Matters Logo" />
          </div>

          <nav className={styles.footerMobileLinks}>
            {footerMobileLinks.map((link) => (
              <Link
                key={link.name}
                to={link.url}
                className={styles.footerMobileLink}
                data-footer-stagger
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className={styles.footerMobileSection} data-footer-stagger>
            <p className={styles.footerMobileSectionTitle}>Connect</p>
            <div className={styles.footerMobileSocial}>
              <a href="https://instagram.com/corkmatters_r">
                <img src={instagramLogo} alt="Instagram" />
              </a>
              <a href="https://www.facebook.com/p/Cork-Matters-100073677396245/">
                <img src={facebookLogo} alt="Facebook" />
              </a>
            </div>
          </div>

          <div className={styles.footerMobileSection} data-footer-stagger>
            <p className={styles.footerMobileSectionTitle}>Newsletter</p>
            <div className={styles.footerMobileNewsletter}>
              {submitStatus === "success" ? (
                <div className={styles.footerMobileSubscribed}>
                  <span>Thank you for subscribing</span>
                  <BiCheck size={24} className={styles.footerMobileCheck} />
                </div>
              ) : (
                <>
                  <input
                    type="email"
                    placeholder="Email address"
                    className={styles.footerMobileEmailInput}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={handleEmailSubmit}
                    disabled={isSubmitting}
                    className={styles.footerMobileNewsletterBtn}
                    aria-label="Subscribe to newsletter"
                  >
                    {isSubmitting ? "..." : <BiRightArrowAlt size={22} />}
                  </button>
                </>
              )}
            </div>
            {submitStatus === "error" && (
              <p className={styles.footerMobileStatusError}>
                Please enter a valid email address.
              </p>
            )}
          </div>
        </div>

        <div className={styles.footerBottom} ref={footerBottomRef}>
          <div className={styles.grid}>
            <div className={styles.footerInfo}>
              <div>
                Copyright © {currentYear} Cork Matters. All rights reserved.
              </div>
            </div>
            <div className={`${styles.footerLinksBase}`}>
              <Link
                to="https://www.conceitoverde.studio"
                className={`${styles.designedBy} ${styles.linkContainer}`}
              >
                <img src={CVLogo} alt="CV Logo" />
                <div className={styles.underlineLinkGrey}>
                  {" "}
                  Designed by Conceito Verde{" "}
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Footer;
